const POSTER_TIME_SECONDS = 1;
const TIMEOUT_MS = 15000;

// Captures a frame from a video as a JPEG data URL. Resolves to null if the video
// cannot be loaded or drawn (e.g. missing CORS headers), so callers can skip it.
export function captureVideoPoster(src: string): Promise<string | null> {
    return new Promise((resolve) => {
        const video = document.createElement("video");
        const timer = setTimeout(() => resolve(null), TIMEOUT_MS);
        const finish = (result: string | null) => {
            clearTimeout(timer);
            video.src = "";
            resolve(result);
        };

        video.src = src;
        video.preload = "metadata";
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.onloadedmetadata = () => {
            video.currentTime = Math.min(POSTER_TIME_SECONDS, video.duration || 0);
        };
        video.onseeked = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext("2d");
                if (!ctx) return finish(null);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                return finish(canvas.toDataURL("image/jpeg", 0.85));
            } catch {
                return finish(null);
            }
        };
        video.onerror = () => finish(null);
    });
}

// Captures posters for a set of video sources, keyed by source. Failed captures are omitted.
export async function captureVideoPosters(sources: string[]): Promise<Record<string, string>> {
    const unique = Array.from(new Set(sources));
    const posters = await Promise.all(unique.map(captureVideoPoster));
    return unique.reduce<Record<string, string>>((acc, src, i) => {
        if (posters[i]) acc[src] = posters[i] as string;
        return acc;
    }, {});
}
